/**
 * "Action required" filter group. Hosts sibling buckets that each render independently; today the
 * Manual Actions bucket, the Errors bucket and the On track bucket.
 *
 * The group is Open-tab only (its buckets describe open RMAs) and shows when at least one bucket is
 * present. Each bucket shows purely on the presence of its own rms-api data — there is no feature
 * check here. rms-api is the single gate for the Manual Actions bucket (ET-3119): it omits
 * `manual_actions` unless the merchant is enrolled in the automation-tracking beta AND its
 * `rma_actions` backfill has finished, so a client-side flag check could only disagree with it. The
 * On track bucket rides the same gate for the same reason — its counts read `rma_actions` too.
 */
import { observer } from "mobx-react";
import "./rma-list-actions-status-filter.scss";
import { IRmaListTab } from "../../../../../../../services/api/rma-list-service";
import { RmaListBucketFiltersStore } from "../../../../../rma-list-page/stores/rma-list-bucket-filters-store";
import RmaListErrorsBucket from "./components/rma-list-errors-bucket/rma-list-errors-bucket";
import RmaListManualActionsBucket from "./components/rma-list-manual-actions-bucket/rma-list-manual-actions-bucket";
import RmaListOnTrackBucket from "./components/rma-list-on-track-bucket/rma-list-on-track-bucket";
import RmaListActionsStatusFilterHeader from "./components/rma-list-actions-status-filter-header/rma-list-actions-status-filter-header";

/** Which bucket triggered the apply; each reports its own analytics event. */
export enum RMA_BUCKET_KIND {
    MANUAL_ACTIONS = "manual_actions",
    ERRORS = "errors",
    ON_TRACK = "on_track",
}

interface IProps {
    bucketFiltersStore: RmaListBucketFiltersStore;
    /** The Open-tab gate is a filters-store concern, so the tab is passed in rather than reached for. */
    currentTab: IRmaListTab;
    disabled: boolean;
    onApply: (bucket: RMA_BUCKET_KIND) => void;
}

const RmaListActionsStatusFilter: React.FC<IProps> = observer(({ bucketFiltersStore, currentTab, disabled, onApply }) => {
    const isOpenTab = currentTab === IRmaListTab.STATUS_GROUP_OPEN;

    const showManualActionsBucket = bucketFiltersStore.hasManualActionsBucket;
    const showErrorsBucket = bucketFiltersStore.hasErrorBucket;
    const showOnTrackBucket = bucketFiltersStore.hasOnTrackBucket;

    if (!isOpenTab || (!showManualActionsBucket && !showErrorsBucket && !showOnTrackBucket)) {
        return null;
    }

    return (
        <div className="rma-list-actions-status-filter">
            <RmaListActionsStatusFilterHeader />
            {showManualActionsBucket && (
                <RmaListManualActionsBucket
                    bucketFiltersStore={bucketFiltersStore}
                    disabled={disabled}
                    onApply={() => onApply(RMA_BUCKET_KIND.MANUAL_ACTIONS)}
                />
            )}
            {showErrorsBucket && (
                <RmaListErrorsBucket
                    bucketFiltersStore={bucketFiltersStore}
                    disabled={disabled}
                    onApply={() => onApply(RMA_BUCKET_KIND.ERRORS)}
                />
            )}
            {showOnTrackBucket && (
                <RmaListOnTrackBucket
                    bucketFiltersStore={bucketFiltersStore}
                    disabled={disabled}
                    onApply={() => onApply(RMA_BUCKET_KIND.ON_TRACK)}
                />
            )}
        </div>
    );
});

export default RmaListActionsStatusFilter;
